import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from 'projects/customer/src/app/shared/services/http-service.service';
import { normalizeAddressBookPayload } from './address-book.utils';
import { AddressBook, AddressBookPayload, ApiEnvelope, BulkDeleteResult, ListParams } from './models/address-book.model';

@Injectable({ providedIn: 'root' })
export class AddressBookService {
    private base = 'frontend/v1/address_books';
    constructor(private httpService: HttpService) {}

    list(params: ListParams): Observable<ApiEnvelope<AddressBook[]>> {
        let path = `${this.base}/get_addresses?page[number]=${params.page}&page[size]=${params.per_page}`;
        if (params.country_id) path += `&filter[country_id]=${params.country_id}`;
        // Backend takes a single string here (not the array-indexed filter[search_pattern][0]
        // shape used by Suppliers) — it does filter.search_pattern.trim(), which throws on an array.
        if (params.search_pattern) path += `&filter[search_pattern]=${encodeURIComponent(params.search_pattern)}`;
        return this.httpService.callAuthGetApi(path);
    }

    getCount(): Observable<ApiEnvelope<AddressBook[]>> {
        return this.list({ page: 1, per_page: 1 });
    }

    get(id: number): Observable<ApiEnvelope<AddressBook>> {
        return this.httpService.callAuthGetApi(`${this.base}/${id}`);
    }

    create(payload: AddressBookPayload): Observable<ApiEnvelope<AddressBook>> {
        return this.httpService.callAuthPostApi(`${this.base}/create`, normalizeAddressBookPayload(payload));
    }

    update(id: number, payload: Partial<AddressBookPayload>): Observable<ApiEnvelope<AddressBook>> {
        return this.httpService.callPutApi(`${this.base}/${id}/update`, normalizeAddressBookPayload(payload));
    }

    remove(id: number): Observable<ApiEnvelope<string>> {
        return this.httpService.callAuthDeleteApi(`${this.base}/${id}/delete`, {});
    }

    /**
     * Soft-deletes many entries in one call. The endpoint caps at 100 ids per
     * request, so larger selections are chunked and the per-chunk results are
     * merged. The response separates ids actually deleted from invalid ones
     * (not found, or belonging to another customer).
     */
    bulkDelete(ids: number[]): Observable<ApiEnvelope<BulkDeleteResult>> {
        const CHUNK = 100;
        if (ids.length <= CHUNK) {
            return this.httpService.callAuthPostApi(`${this.base}/bulk_delete`, {
                address_book_ids: ids
            });
        }
        const chunks: number[][] = [];
        for (let i = 0; i < ids.length; i += CHUNK) {
            chunks.push(ids.slice(i, i + CHUNK));
        }
        return forkJoin(
            chunks.map((c) =>
                this.httpService.callAuthPostApi(`${this.base}/bulk_delete`, {
                    address_book_ids: c
                })
            )
        ).pipe(
            map((responses: ApiEnvelope<BulkDeleteResult>[]) => {
                const merged: BulkDeleteResult = {
                    deleted_count: 0,
                    deleted_ids: [],
                    invalid_ids: []
                };
                let last = responses[responses.length - 1];
                responses.forEach((r) => {
                    const d = r?.data;
                    if (!d) return;
                    merged.deleted_count += d.deleted_count ?? d.deleted_ids?.length ?? 0;
                    merged.deleted_ids.push(...(d.deleted_ids ?? []));
                    merged.invalid_ids.push(...(d.invalid_ids ?? []));
                });
                return { ...last, data: merged };
            })
        );
    }

    // Multipart upload — field name `excelfile` is applied automatically by callUploadFileServiceApi.
    bulkUpload(file: File): Observable<any> {
        return this.httpService.callUploadFileServiceApi('frontend/v1/address_books/bulk_upload', file);
    }

    getTemplate(): Observable<Blob> {
        return this.httpService.getFileAsBlob('v1/masters/templates?type=21');
    }
}
